import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { 
  Play, 
  Check, 
  X, 
  Edit3, 
  Save, 
  AlertCircle,
  CheckCircle,
  Clock,
  Trash2,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import './TuneYourAgent.css';

const TuneYourAgent = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editingAnswer, setEditingAnswer] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showRespondedQuestions, setShowRespondedQuestions] = useState(false);
  const [hasContent, setHasContent] = useState(false);
  const [checkingContent, setCheckingContent] = useState(true);

  // Load existing questions and check content on component mount
  useEffect(() => {
    loadQuestions();
    checkContent();
  }, []);

  const loadQuestions = async () => {
    try {
      const response = await fetch('/api/questions/questions', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Loaded questions:', data.questions);
        setQuestions(data.questions || []);
      }
    } catch (error) {
      console.error('Error loading questions:', error);
    }
  };

  const checkContent = async () => {
    try {
      setCheckingContent(true);
      const response = await fetch('/api/questions/has-content', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setHasContent(data.hasContent);
      }
    } catch (error) {
      console.error('Error checking content:', error);
    } finally {
      setCheckingContent(false);
    }
  };

  const generateQuestions = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch('/api/questions/generate-questions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setQuestions(data.questions || []);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to generate questions');
      }
    } catch (error) {
      console.error('Error generating questions:', error);
      setError('Failed to generate questions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (questionId) => {
    setEditingQuestion(questionId);
    const question = questions.find(q => q.id === questionId);
    setEditingAnswer(question?.answer || '');
  };

  const cancelEditing = () => {
    setEditingQuestion(null);
    setEditingAnswer('');
  };

  const saveAnswer = async (questionId) => {
    setSaving(true);
    try {
      const response = await fetch(`/api/questions/questions/${questionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          status: 'approved',
          answer: editingAnswer
        })
      });

      if (response.ok) {
        // Reload questions to get updated data
        await loadQuestions();
        setEditingQuestion(null);
        setEditingAnswer('');
        setSuccess('Answer saved successfully!');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save answer');
      }
    } catch (error) {
      console.error('Error saving answer:', error);
      setError('Failed to save answer. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const approveAnswer = async (questionId) => {
    setSaving(true);
    try {
      // Get the current question to preserve its answer
      const question = questions.find(q => q.id === questionId);
      
      const response = await fetch(`/api/questions/questions/${questionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          status: 'approved',
          answer: question?.answer || ''
        })
      });

      if (response.ok) {
        await loadQuestions();
        
        // Note: We intentionally don't process approved questions immediately here.
        // Processing happens when the user is idle or navigates away from the page
        // to avoid overwhelming the system with multiple rapid API calls.
        
        setSuccess('Answer approved!');
    
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to approve answer');
      }
    } catch (error) {
      console.error('Error approving answer:', error);
      setError('Failed to approve answer. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const rejectAnswer = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) {
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`/api/questions/questions/${questionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        await loadQuestions();
        setSuccess('Question deleted!');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to delete question');
      }
    } catch (error) {
      console.error('Error deleting question:', error);
      setError('Failed to delete question. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Process approved questions when user is idle or navigates away from the page
  useEffect(() => {
    const processApprovedQuestions = async () => {
      const approvedQuestions = questions.filter(q => q.status === 'approved' && q.answer);
      
      if (approvedQuestions.length > 0) {
        try {
          await fetch('/api/questions/process-approved-questions', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
        } catch (error) {
          console.error('Error processing approved questions:', error);
        }
      }
    };

    // Process on component unmount (when user navigates away)
    return () => {
      processApprovedQuestions();
    };
  }, [questions]);

  // Separate questions into responded and unresponded
  const respondedQuestions = questions.filter(q => q.status === 'approved' || q.status === 'rejected');
  const unrespondedQuestions = questions.filter(q => !q.status || q.status === 'pending');

  return (
    <div className="tune-your-agent" data-theme="dark">
      {error && (
        <div className="alert alert-error">
          <AlertCircle size={16} />
          {error}
        </div>
      )}
      
      {success && (
        <div className="alert alert-success">
          <CheckCircle size={16} />
          {success}
        </div>
      )}

      {questions.length === 0 && !checkingContent && (
        <div className="tune-actions">
          <div className="empty-state">
            <h3>No Questions Generated Yet</h3>
            <p>Click "Generate Questions" to create questions that will be used to train your agent.</p>
            {!hasContent && (
              <p className="no-content-message">This feature is only available once you've uploaded files, webpages, or freeform text to your agent.</p>
            )}
            {hasContent && (
              <button 
                className="btn btn-primary"
                onClick={generateQuestions}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Clock size={16} />
                    Generating Questions...
                  </>
                ) : (
                  <>
                    <Play size={16} />
                    Generate Questions
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )}

      {questions.length > 0 && (
        <div className="questions-list">
          {respondedQuestions.length > 0 && (
            <div className="responded-questions-section">
              <button 
                className="accordion-header"
                onClick={() => setShowRespondedQuestions(!showRespondedQuestions)}
              >
                <h3>Responded Questions ({respondedQuestions.length})</h3>
                {showRespondedQuestions ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
              
              {showRespondedQuestions && (
                <div className="responded-questions">
                  {respondedQuestions.map((question) => (
                    <div key={`responded-${question.id}`} className="question-card">
                      <div className="question-header">
                        <h4>{question.question}</h4>
                        <button 
                          className="trash-button"
                          onClick={() => rejectAnswer(question.id)}
                          disabled={saving}
                          title="Delete this question"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>

                      <div className="question-content">
                        {editingQuestion === question.id ? (
                          <div className="edit-answer">
                            <textarea
                              value={editingAnswer}
                              onChange={(e) => setEditingAnswer(e.target.value)}
                              placeholder="Enter your answer here..."
                              rows={4}
                            />
                            <div className="edit-actions">
                              <button 
                                className="btn btn-sm btn-primary"
                                onClick={() => saveAnswer(question.id)}
                                disabled={saving}
                              >
                                <Save size={14} />
                                Save
                              </button>
                              <button 
                                className="btn btn-sm btn-secondary"
                                onClick={cancelEditing}
                              >
                                <X size={14} />
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="answer-section">
                            {question.answer ? (
                              <div className="answer-content">
                                <ReactMarkdown>{question.answer}</ReactMarkdown>
                                <div className="answer-actions">
                                  <button 
                                    className="btn btn-sm btn-secondary"
                                    onClick={() => startEditing(question.id)}
                                  >
                                    <Edit3 size={14} />
                                    Edit
                                  </button>
                                  <span className={`status-badge status-${question.status}`}>
                                    {question.status}
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <div className="no-answer">
                                <p>No answer available. Click "Edit" to provide an answer.</p>
                                <button 
                                  className="btn btn-sm btn-primary"
                                  onClick={() => startEditing(question.id)}
                                >
                                  <Edit3 size={14} />
                                  Add Answer
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {unrespondedQuestions.length > 0 && (
            <div className="unresponded-questions-section">
              <h3>Questions to Review ({unrespondedQuestions.length})</h3>
              
              {unrespondedQuestions.map((question) => (
                <div key={`unresponded-${question.id}`} className="question-card">
                  <div className="question-header">
                    <h4>{question.question}</h4>
                    <button 
                      className="trash-button"
                      onClick={() => rejectAnswer(question.id)}
                      title="Reject this question"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  <div className="question-content">
                    {editingQuestion === question.id ? (
                      <div className="edit-answer">
                        <textarea
                          value={editingAnswer}
                          onChange={(e) => setEditingAnswer(e.target.value)}
                          placeholder="Enter your answer here..."
                          rows={4}
                        />
                        <div className="edit-actions">
                          <button 
                            className="btn btn-sm btn-primary"
                            onClick={() => saveAnswer(question.id)}
                            disabled={saving}
                          >
                            <Save size={14} />
                            Save
                          </button>
                          <button 
                            className="btn btn-sm btn-secondary"
                            onClick={cancelEditing}
                          >
                            <X size={14} />
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="answer-section">
                        {question.answer ? (
                          <div className="answer-content">
                            <ReactMarkdown>{question.answer}</ReactMarkdown>
                            <div className="answer-actions">
                              <button 
                                className="btn btn-sm btn-secondary"
                                onClick={() => startEditing(question.id)}
                              >
                                <Edit3 size={14} />
                                Edit
                              </button>
                              <button 
                                className="btn btn-sm btn-success"
                                onClick={() => approveAnswer(question.id)}
                                disabled={saving}
                              >
                                <Check size={14} />
                                Approve
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="no-answer">
                            <p>No answer generated. Click "Edit" to provide an answer.</p>
                            <button 
                              className="btn btn-sm btn-primary"
                              onClick={() => startEditing(question.id)}
                            >
                              <Edit3 size={14} />
                              Add Answer
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {questions.length > 0 && (
            <div className="tune-actions bottom">
              <button 
                className="btn btn-primary"
                onClick={generateQuestions}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Clock size={16} />
                    Generating Questions...
                  </>
                ) : (
                  <>
                    <Play size={16} />
                    Generate More Questions
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default TuneYourAgent;
